import { disableBodyScroll, enableBodyScroll } from '~js/utils/dom';
import { renderDrawerModal } from '../sections/side-drawer-product';

/* Represents the state of the quick add product drawer. */
const QuickAddProductSideDrawer = {
  overlay: null,
  drawer: null,
  section: null,
  isLoading: false,
  handle: null,
  closeButton: null,
  isPDP: false,
  ifdisplayedProductClicked: false,
  productClickedInPdp: false,

  // Initialize the drawer and its functionality
  initialize() {
    this.initializeElements();
    this.hide();
    this.initializeCloseButton();

    // Expose trigger and hide functions for external access
    window.QuickBuy = {
      trigger: this.trigger.bind(this),
      hide: this.hide.bind(this),
    };
    // Attach resize event listener to handle hiding on larger screens
    if (window.location.pathname.includes('/products/')) {
      window.addEventListener('resize', this.handleResize.bind(this));

      // Initial check for the screen size
      this.handleResize();
    }
  },

  // initialize elements
  initializeElements() {
    this.overlay = document.querySelector('.js-quick-add-drawer-overlay');
    this.drawer = document.querySelector('.js-quick-add-product-drawer');
    this.section = this.drawer.querySelector('.js-drawer-section-content');
    this.closeButton = this.drawer.querySelector(
      '.js-quick-add-drawer-close-btn',
    );
  },

  // Show the drawer by manipulating CSS classes
  show() {
    this.drawer.classList.add('is-open');
    this.overlay.classList.add('is-open');
    // Dispatch a custom event to update the PDP drawer based on pdp options selected
    if (this.isPDP || this.productClickedInPdp) {
      document.body.style.overflow = 'hidden';
      window.dispatchEvent(new CustomEvent('pdpVariantChange'));
      return;
    }

    disableBodyScroll();
  },

  // Hide the drawer by manipulating CSS classes
  hide() {
    this.drawer.classList.remove('is-open');
    this.overlay.classList.remove('is-open');

    // Dispatch a custom event to update the PDP variant selection
    if (this.isPDP || this.productClickedInPdp) {
      document.body.style.overflow = '';
      window.dispatchEvent(new CustomEvent('syncVariantWithURL'));
      return;
    }

    enableBodyScroll();
  },

  // Initialize event listeners for the close button
  initializeCloseButton() {
    const closeDrawer = () => {
      this.hide(); // Call hide method to close the drawer
    };

    if (this.closeButton === null) return;
    this.closeButton.addEventListener('click', closeDrawer);
    this.overlay.addEventListener('click', () => {
      closeDrawer();
    });
  },

  /**
   * Trigger method to handle showing the drawer with the appropriate Shopify product data.
   * @param {String} productHandle - product handle that is used to render the section and get the product data.
   * @param {String} isPDP - isPDP is used to maintain the drawer rendering logic w.r.t screen width which is different in PLP and PDP.
   * @param {String} ifdisplayedProductClicked - since we are updating the url in the pdp when the main pdp sticky button opens the quick drawer, we need to prevent the other section add to bag button from updating the url.
   */
  async trigger(
    productHandle,
    isPDP = false,
    ifdisplayedProductClicked = false,
    productClickedInPdp = false,
  ) {
    // If the handle is the same, just show the drawer
    if (this.handle === productHandle) {
      this.show();
      return;
    }

    if (this.isLoading) return;
    this.isLoading = true;
    this.handle = productHandle;
    this.isPDP = isPDP;
    this.ifdisplayedProductClicked = ifdisplayedProductClicked;
    this.productClickedInPdp = productClickedInPdp;
    try {
      const res = await fetch(
        `/products/${this.handle}?section_id=side-drawer-product`,
      );
      if (!res.ok) throw new Error('Fetch of quickAddProductDrawer failed');

      const productHTML = await res.text();
      renderDrawerModal(
        this.section,
        productHTML,
        this.isPDP,
        ifdisplayedProductClicked,
        this.productClickedInPdp,
      );
      this.show();
    } catch (err) {
      console.error(err.message);
      alert("Sorry, we couldn't load the Quick Add Product feature");
    } finally {
      this.isLoading = false;
    }
  },

  // Handle hiding the drawer on large screens
  handleResize() {
    if (
      window.innerWidth >= 1024 &&
      this.drawer.classList.contains('is-open')
    ) {
      this.hide();
    }
  },
};

export { QuickAddProductSideDrawer };
